import { supabase } from '@/lib/supabase';

/**
 * Seguimiento del sitio público.
 *
 * FUENTE ÚNICA DE VERDAD: system_settings → tracking_pixels (columna `value`, JSONB).
 *
 * Reglas de este módulo:
 * - El píxel de Meta se inicializa UNA sola vez por carga del sitio.
 * - Se envía un único PageView por navegación real de React Router.
 * - En /admin y /client no se inicializa el píxel ni se envían eventos de Meta.
 * - Google Analytics y Google Tag Manager conservan su comportamiento previo
 *   (inicialización única, sin cambios en sus eventos).
 */

interface TrackingPixels {
  facebookPixelId: string;
  googleAnalyticsId: string;
  googleTagManagerId: string;
}

const PRIVATE_PREFIXES = ['/admin', '/client'];

/** Configuración leída una sola vez por carga (promesa compartida). */
let configPromise: Promise<TrackingPixels | null> | null = null;

/** El script de Meta sólo se inyecta una vez por carga. */
let metaInitialized = false;

/** Última ruta que ya envió PageView (evita duplicados por re-render/StrictMode). */
let lastPageViewPath: string | null = null;

/** Otros trackers: inicialización única. */
let gaInitialized = false;
let gtmInitialized = false;

const isPrivatePath = (path: string): boolean => {
  if (!path) return false;
  return PRIVATE_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
};

const parsePixels = (raw: unknown): TrackingPixels | null => {
  if (raw === null || raw === undefined) return null;

  let value: Record<string, unknown> | null = null;

  if (typeof raw === 'string') {
    const text = raw.trim();
    if (!text) return null;
    try {
      value = JSON.parse(text) as Record<string, unknown>;
    } catch {
      return null;
    }
  } else if (typeof raw === 'object') {
    value = raw as Record<string, unknown>;
  }

  if (!value) return null;

  const pick = (key: string): string => {
    const item = (value as Record<string, unknown>)[key];
    return typeof item === 'string' ? item.trim() : '';
  };

  return {
    facebookPixelId: pick('facebookPixelId'),
    googleAnalyticsId: pick('googleAnalyticsId'),
    googleTagManagerId: pick('googleTagManagerId'),
  };
};

const loadPixelsConfig = (): Promise<TrackingPixels | null> => {
  if (!configPromise) {
    configPromise = (async () => {
      try {
        const { data, error } = await supabase
          .from('system_settings')
          .select('*')
          .eq('setting_key', 'tracking_pixels')
          .maybeSingle();

        if (error) {
          console.error('Error cargando tracking_pixels:', error);
          return null;
        }

        if (!data) return null;

        const row = data as Record<string, unknown>;
        const raw = row.setting_value ?? row.value ?? row.data ?? row.json ?? null;

        return parsePixels(raw);
      } catch (error) {
        console.error('Error al leer la configuración de tracking:', error);
        return null;
      }
    })();
  }

  return configPromise;
};

interface FacebookQueue {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  loaded?: boolean;
  version?: string;
  push?: unknown;
}

const getFbq = (): FacebookQueue | null => {
  const win = window as unknown as { fbq?: FacebookQueue };
  return win.fbq ?? null;
};

/** Inyecta el script de Meta y crea la cola estándar. Idempotente. */
const ensureFacebookSnippet = (): void => {
  const win = window as unknown as { fbq?: FacebookQueue; _fbq?: unknown };

  if (win.fbq) {
    return;
  }

  const fbq = function (this: unknown, ...args: unknown[]): void {
    const queueable = fbq as unknown as FacebookQueue;
    if (queueable.callMethod) {
      queueable.callMethod.apply(fbq, args);
    } else {
      queueable.queue.push(args);
    }
  } as FacebookQueue;

  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];

  win.fbq = fbq;
  if (!win._fbq) {
    win._fbq = fbq;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
};

/** Fallback sin JavaScript. Sólo aplica a navegadores con JS desactivado. */
const appendFacebookNoscript = (pixelId: string): void => {
  const container = document.body ?? document.documentElement;
  if (!container) return;

  const noscript = document.createElement('noscript');
  const img = document.createElement('img');
  img.height = 1;
  img.width = 1;
  img.style.display = 'none';
  img.alt = '';
  img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  container.appendChild(noscript);
};

/**
 * Inicializa el píxel si hace falta y envía PageView una sola vez por ruta.
 * Nunca actúa en rutas privadas.
 */
const syncMetaPageView = async (path: string): Promise<void> => {
  if (isPrivatePath(path)) {
    return;
  }

  const pixels = await loadPixelsConfig();
  const pixelId = pixels?.facebookPixelId ?? '';

  if (!pixelId) {
    return;
  }

  if (!metaInitialized) {
    ensureFacebookSnippet();

    const fbq = getFbq();
    if (!fbq) {
      return;
    }

    fbq('init', pixelId);
    appendFacebookNoscript(pixelId);
    metaInitialized = true;
  }

  const fbq = getFbq();
  if (!fbq) {
    return;
  }

  if (lastPageViewPath === path) {
    return;
  }

  lastPageViewPath = path;
  fbq('track', 'PageView');
};

const initGoogleAnalytics = (measurementId: string): void => {
  if (gaInitialized || !measurementId) {
    return;
  }
  gaInitialized = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  const win = window as unknown as {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  };

  win.dataLayer = win.dataLayer ?? [];
  const dataLayer = win.dataLayer;

  win.gtag =
    win.gtag ??
    function gtag(...args: unknown[]): void {
      dataLayer.push(args);
    };

  win.gtag('js', new Date());
  win.gtag('config', measurementId);
};

const initGoogleTagManager = (containerId: string): void => {
  if (gtmInitialized || !containerId) {
    return;
  }
  gtmInitialized = true;

  const win = window as unknown as { dataLayer?: unknown[] };
  win.dataLayer = win.dataLayer ?? [];
  win.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const firstScript = document.getElementsByTagName('script')[0];
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;

  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }

  const container = document.body ?? document.documentElement;
  if (container) {
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
    iframe.height = 0;
    iframe.width = 0;
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    container.appendChild(noscript);
  }
};

/**
 * Registra un PageView de Meta para la ruta indicada.
 * Llamado en cada navegación de React Router (y en la carga inicial).
 */
export async function trackMetaPageView(path: string): Promise<void> {
  try {
    await syncMetaPageView(path);
  } catch (error) {
    console.warn('No se pudo registrar el PageView de Meta:', error);
  }
}

/**
 * Carga inicial del sitio público: lee la configuración una sola vez
 * e inicializa los trackers habilitados.
 */
export async function loadTrackingPixels(): Promise<void> {
  const path = window.location.pathname;

  if (isPrivatePath(path)) {
    return;
  }

  const pixels = await loadPixelsConfig();
  if (!pixels) {
    return;
  }

  initGoogleAnalytics(pixels.googleAnalyticsId);
  initGoogleTagManager(pixels.googleTagManagerId);

  await trackMetaPageView(path);
}